/**
 * src/pages/MyAccount.jsx — Call It In LLC — Authenticated Account View
 *
 * Displays the identity and settings for the currently authenticated user.
 * Data is sourced from GET /auth/me (live) and the session cache (immediate).
 * Business settings (rates, drivers, employees) are NOT included here.
 * Those belong in the Settings → Business section accessible via the sidebar.
 */

import React, { useEffect, useState, useCallback } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "layouts/dashboards/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getCurrentUser } from "data/users";
import { request } from "../api/client";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatRole(role) {
  const map = { admin: "Admin", dispatcher: "Dispatcher", driver: "Driver" };
  return map[role] || role || "—";
}

function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    return new Date(isoString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return isoString;
  }
}

const ROLE_COLOR = { admin: "error", dispatcher: "warning", driver: "info" };

// ── Sub-components ────────────────────────────────────────────────────────────

function AccountHeader({ profile }) {
  const displayName = profile?.name || profile?.email || "My Account";
  const initials = getInitials(displayName);
  const role = profile?.role;

  return (
    <Card>
      <MDBox p={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <MDBox
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #334155 0%, #475569 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              <MDTypography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#fff", lineHeight: 1 }}
              >
                {initials}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs>
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1E293B" }}>
              {displayName}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
              {role && (
                <Chip
                  label={formatRole(role)}
                  color={ROLE_COLOR[role] || "default"}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                />
              )}
              <MDTypography variant="caption" sx={{ color: "#64748b" }}>
                {profile?.email}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

function AccountDetails({ profile }) {
  const rows = [
    { label: "Display Name", value: profile?.name || "—" },
    { label: "Email Address", value: profile?.email || "—" },
    { label: "Role", value: formatRole(profile?.role) },
    { label: "Account ID", value: profile?.id || "—" },
    { label: "Member Since", value: formatDate(profile?.created_at) },
    { label: "Organization", value: profile?.organization_id || "—" },
  ];

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E293B", mb: 0.5 }}>
          Account Details
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: "#64748b" }}>
          Read-only. Contact your administrator to update your name, email, or role.
        </MDTypography>
        <MDBox mt={2}>
          {rows.map(({ label, value }) => (
            <MDBox
              key={label}
              display="flex"
              alignItems="flex-start"
              py={1}
              sx={{ borderBottom: "1px solid #f1f5f9", "&:last-child": { borderBottom: "none" } }}
            >
              <MDTypography
                variant="caption"
                fontWeight="bold"
                sx={{ color: "#334155", minWidth: 140, flexShrink: 0 }}
              >
                {label}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#475569", wordBreak: "break-all" }}>
                {value}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    </Card>
  );
}

function ChangePasswordCard({ userId }) {
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.newPassword || form.newPassword.length < 8) {
      setNotice({ severity: "error", msg: "Password must be at least 8 characters." });
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setNotice({ severity: "error", msg: "Passwords do not match." });
      return;
    }

    setSaving(true);
    try {
      const res = await request(`/auth/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: form.newPassword }),
      });

      if (res.ok) {
        setForm({ newPassword: "", confirmPassword: "" });
        setNotice({ severity: "success", msg: "Password updated successfully." });
      } else {
        const err = await res.json().catch(() => ({}));
        setNotice({
          severity: "error",
          msg: err?.detail || "Password update failed. Please try again.",
        });
      }
    } catch {
      setNotice({ severity: "error", msg: "Network error. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E293B", mb: 0.5 }}>
          Change Password
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: "#64748b" }}>
          Minimum 8 characters. This action takes effect immediately.
        </MDTypography>

        {notice && (
          <Alert
            severity={notice.severity}
            onClose={() => setNotice(null)}
            sx={{ mt: 2, mb: 1 }}
          >
            {notice.msg}
          </Alert>
        )}

        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={6}>
            <MDInput
              type="password"
              label="New Password"
              fullWidth
              value={form.newPassword}
              onChange={handleChange("newPassword")}
              inputProps={{ autoComplete: "new-password" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              type="password"
              label="Confirm New Password"
              fullWidth
              value={form.confirmPassword}
              onChange={handleChange("confirmPassword")}
              inputProps={{ autoComplete: "new-password" }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={saving || !form.newPassword}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : "Update Password"}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

function CompanySettingsNote({ isAdmin }) {
  return (
    <Card>
      <MDBox p={3} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <MDBox flex={1}>
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E293B", mb: 0.5 }}>
            Company Settings
          </MDTypography>
          {isAdmin ? (
            <MDTypography variant="body2" sx={{ color: "#475569" }}>
              Base rates, per-mile rates, fuel surcharge, weight rules, wait-time rules, and
              special service rates are configured in Company Settings.
              Employee and driver account management is under{" "}
              <MDTypography
                component="a"
                href="#/employees"
                variant="body2"
                sx={{ color: "#3b82f6", fontWeight: 600 }}
              >
                Employees
              </MDTypography>
              .
            </MDTypography>
          ) : (
            <MDTypography variant="body2" sx={{ color: "#475569" }}>
              Company-wide pricing rates, mileage rules, and service configuration are managed by
              your administrator. Contact your admin to request changes.
            </MDTypography>
          )}
        </MDBox>
        {isAdmin && (
          <MDBox flexShrink={0} pt={0.5}>
            <MDButton
              component="a"
              href="#/administration/company-settings"
              variant="outlined"
              color="info"
              size="small"
            >
              Open Company Settings
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MyAccount() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [toast, setToast] = useState({ open: false, color: "success", title: "", content: "" });

  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await request("/auth/me");
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      } else {
        setProfile(getCurrentUser());
        setFetchError("Could not reach the server. Showing cached session data.");
      }
    } catch {
      setProfile(getCurrentUser());
      setFetchError("Network error. Showing cached session data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const cachedUser = getCurrentUser();
  const isAdmin = (profile?.role || cachedUser?.role) === "admin";
  const userId = profile?.id || cachedUser?.id;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={2} sx={{ maxWidth: 800, mx: "auto" }}>
        <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1E293B", mb: 0.5 }}>
          My Account
        </MDTypography>
        <MDTypography variant="body2" sx={{ color: "#475569", mb: 3 }}>
          Identity and settings for your currently authenticated account.
        </MDTypography>

        {fetchError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {fetchError}
          </Alert>
        )}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AccountHeader profile={profile} />
            </Grid>
            <Grid item xs={12}>
              <AccountDetails profile={profile} />
            </Grid>
            {isAdmin && userId && (
              <Grid item xs={12}>
                <ChangePasswordCard userId={userId} />
              </Grid>
            )}
            {!isAdmin && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1E293B", mb: 0.5 }}>
                      Password
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: "#475569" }}>
                      To change your password, contact your administrator.
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            )}
            <Grid item xs={12}>
              <CompanySettingsNote isAdmin={isAdmin} />
            </Grid>
          </Grid>
        )}
      </MDBox>

      <MDSnackbar
        color={toast.color}
        icon={toast.color === "success" ? "check" : "warning"}
        title={toast.title}
        content={toast.content}
        open={toast.open}
        onClose={closeToast}
        close={closeToast}
        bgWhite
      />
    </DashboardLayout>
  );
}
